import PropTypes from '~/lib/propTypes'
import styled from '@emotion/styled'
import {
  Button,
  color,
  DialogAdornment,
  DialogBody,
  DialogHeader,
  DialogWindow,
  ContentTransition,
  space,
  Text,
  useDialog,
  InputV2 as Input,
  Alert,
} from '@ticketswap/solar'
import { ChevronLeftAlt, CloseAlt } from '@ticketswap/solar/icons'
import { useFormik } from 'formik'
import * as yup from 'yup'
import EmailSent from '~/components/EmailSentIllustration'
import { createMarkup } from '~/utils/createMarkup'
import { track } from '~/utils/analytics'
import { useMutation } from '@apollo/client'
import { SOCIAL_DISCONNECT_FACEBOOK, SOCIAL_DISCONNECT_TWITTER } from '~/lib/tracking-events'
import useIsMounted from '~/hooks/useIsMounted'
import { useState } from 'react'
import { START_EMAIL_VERIFICATION } from '~/graphql/mutations/startEmailVerification'
import { UNLINK_FACEBOOK_ACCOUNT } from '~/graphql/mutations/unlinkFacebookAccount'
import { REMOVE_TWITTER_ACCOUNT } from '~/graphql/mutations/removeTwitterAccount'
import useLoggedInUser from '~/hooks/useLoggedInUser'

const Illustration = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-block-end: ${space[32]};
`

const StyledText = styled(Text)`
  color: ${color.foregroundMuted};
  margin-block-end: ${space[16]};
`

const ConfirmMessage = styled.div`
  margin-block-end: ${space[16]};
  text-align: center;

  strong {
    display: inline-block;
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
`
const DisconnectBtn = styled(Button)`
  color: ${color.mars};

  &:hover,
  &:active {
    color: ${color.mars};
  }
`
const StyledButton = styled(Button)`
  margin-block-start: ${space[16]};
  width: 100%;
`
const StyledAlert = styled(Alert)`
  margin-block-start: ${space[16]};
`

const ActionButton = styled.button`
  color: ${color.foreground};
`

const SocialAccountsView = ({ error, handleDisconnect, hide, loading, socialTitle, t }) => (
  <DialogBody>
    <Content>
      <StyledText>{t('common:socialConnect.dialog.text')}</StyledText>
      <DisconnectBtn variant="inverted" onClick={handleDisconnect} loading={loading}>
        {t('common:socialConnect.disconnect', { socialTitle })}
      </DisconnectBtn>
      <StyledButton onClick={hide}>{t('common:socialConnect.dialog.confirm')}</StyledButton>
      {error && <StyledAlert variant="error">{error}</StyledAlert>}
    </Content>
  </DialogBody>
)

const CantDisconnectView = ({ showVerifyEmail, t }) => (
  <DialogBody>
    <Content>
      <StyledText>{t('common:socialConnect.dialog.cantDisconnect.text')}</StyledText>
      <StyledButton onClick={showVerifyEmail}>
        {t('common:socialConnect.dialog.cantDisconnect.action')}
      </StyledButton>
    </Content>
  </DialogBody>
)

const VerifyEmailView = ({ email, setActiveView, t, userId }) => {
  const [error, setError] = useState(null)
  const [emailVerify, { loading }] = useMutation(START_EMAIL_VERIFICATION)

  const formik = useFormik({
    initialValues: {
      email: email || '',
    },
    validationSchema: yup.object().shape({
      email: yup.string().email().required(),
    }),
    onSubmit: async values => {
      try {
        setError(null)
        const {
          data: { startUserEmailVerification: res },
        } = await emailVerify({
          variables: {
            input: {
              userId,
              email: values.email,
            },
          },
        })
        if (res.errors.length > 0) {
          setError(res.errors[0].message)
        } else {
          return setActiveView('verifyEmailConfirmation')
        }
      } catch (error) {
        console.error(error)
      }
    },
  })

  const { dirty, handleChange, handleSubmit, isValid, values } = formik

  return (
    <DialogBody>
      <Content>
        <StyledText>{t('common:socialConnect.dialog.verifyEmail.text')}</StyledText>
        <form onSubmit={handleSubmit}>
          <Input
            id="email"
            type="email"
            aria-label="Verify email"
            value={values.email}
            onChange={event => {
              handleChange(event)
              setError(null)
            }}
          />
          <StyledButton loading={loading} type="submit" disabled={!isValid && dirty}>
            {t('common:socialConnect.dialog.verifyEmail.action')}
          </StyledButton>
          {((!isValid && dirty) || error) && (
            <StyledAlert variant="error">
              {t('common:socialConnect.dialog.verifyEmail.validationError') || error}
            </StyledAlert>
          )}
        </form>
      </Content>
    </DialogBody>
  )
}

export const VerifyEmailConfirmation = ({ hide, t }) => {
  const { email } = useLoggedInUser()

  return (
    <DialogBody>
      <Content>
        <Illustration>
          <EmailSent />
        </Illustration>
        <ConfirmMessage>
          <p
            dangerouslySetInnerHTML={createMarkup(
              t('common:socialConnect.dialog.verifyEmailConfirmation.text', {
                email,
              }),
              { renderInline: true, disableAutolink: true }
            )}
          />
        </ConfirmMessage>
        <Button fullWidth onClick={hide}>
          {t('common:socialConnect.dialog.verifyEmailConfirmation.action')}
        </Button>
      </Content>
    </DialogBody>
  )
}

function SocialAccountsInformationDialog({
  children,
  email,
  isEmailVerified = false,
  socialTitle,
  t,
  userId,
}) {
  const { show, hide, getToggleProps, getWindowProps } = useDialog({
    onToggle: on => {
      if (on) {
        setActiveView(initialView)
      }
    },
  })
  const isMounted = useIsMounted()
  const [title, setTitle] = useState('')
  const [back, setBack] = useState('')
  const initialView = 'socialAccounts'
  const [activeView, setActiveView] = useState(initialView)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [unlinkFacebook] = useMutation(UNLINK_FACEBOOK_ACCOUNT)
  const [removeTwitter] = useMutation(REMOVE_TWITTER_ACCOUNT)

  const handleDisconnect = async () => {
    if (socialTitle === 'Facebook') {
      if (!isEmailVerified) return setActiveView('cantDisconnect')

      setLoading(true)
      const {
        data: {
          unlinkFacebookAccount: { errors },
        },
      } = await unlinkFacebook()

      if (errors.length) {
        setError(errors[0].message)
      }

      track(SOCIAL_DISCONNECT_FACEBOOK)
    }

    if (socialTitle === 'Twitter') {
      setLoading(true)
      const {
        data: {
          removeTwitterAccount: { errors },
        },
      } = await removeTwitter({
        variables: {
          input: {
            userId,
          },
        },
      })

      if (errors.length) {
        setError(errors[0].message)
      }

      track(SOCIAL_DISCONNECT_TWITTER)
    }

    isMounted() && hide()
    isMounted() && setLoading(false)
  }

  return (
    <>
      {children({ show, getToggleProps })}
      <DialogWindow {...getWindowProps()}>
        <DialogHeader>
          {back && (
            <DialogAdornment left>
              <ActionButton onClick={() => setActiveView(back)}>
                <ChevronLeftAlt size={24} />
              </ActionButton>
            </DialogAdornment>
          )}
          {title}
          <DialogAdornment right>
            <ActionButton onClick={hide}>
              <CloseAlt size={24} />
            </ActionButton>
          </DialogAdornment>
        </DialogHeader>
        <ContentTransition
          onChange={item => {
            setTitle(item.props.title)
            setBack(item.props.back)
          }}
          activeView={activeView}
        >
          <SocialAccountsView
            key="socialAccounts"
            error={error}
            handleDisconnect={handleDisconnect}
            hide={hide}
            loading={loading}
            socialTitle={socialTitle}
            t={t}
            title={t('common:socialConnect.dialog.title')}
          />
          <CantDisconnectView
            key="cantDisconnect"
            back="socialAccounts"
            showVerifyEmail={() => setActiveView('verifyEmail')}
            t={t}
            title={t('common:socialConnect.dialog.cantDisconnect.title')}
          />
          <VerifyEmailView
            key="verifyEmail"
            back="cantDisconnect"
            email={email}
            setActiveView={setActiveView}
            t={t}
            title={t('common:socialConnect.dialog.verifyEmail.title')}
            userId={userId}
          />
          <VerifyEmailConfirmation
            key="verifyEmailConfirmation"
            back="verifyEmail"
            hide={hide}
            t={t}
            title={t('common:socialConnect.dialog.verifyEmailConfirmation.title')}
          />
        </ContentTransition>
      </DialogWindow>
    </>
  )
}

SocialAccountsInformationDialog.propTypes = {
  children: PropTypes.func.isRequired,
  email: PropTypes.string,
  isEmailVerified: PropTypes.bool,
  socialTitle: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
}
export default SocialAccountsInformationDialog
